import * as React from 'react';
import { debounce, isString } from 'lodash';
import { Button, Col, Form, Icon, Input, Modal, Radio, Row, Select, Spin, Steps, Upload, message, DatePicker, Checkbox, Tooltip } from 'antd';
import { FormComponentProps, FormItemProps } from 'antd/lib/form';
import { RouteComponentProps } from 'react-router';
import { IDrugStoreInfo } from 'src/entity/DrugStoreEntity';
import { IAgentInfo } from 'src/entity/AgentEntity';
import I18N from 'src/services/I18NService';
import WalletService, { IAccountInfo, EAccountMemberType, ERegisterStep, EEnterpriseRegisterStep } from 'src/services/WalletService';
import { RouterConfig } from 'src/routers/urlConfig';
import TencentCaptchaButton from 'src/components/common/TencentCaptchaButton';
import { IVerificationCode } from 'src/entity/LoginParam';
import FlexApiService, { IBucketInfo } from 'src/services/FlexApiService';
import moment from 'moment';
import LocationService from 'src/services/LocationService';
import { CheckboxChangeEvent } from 'antd/lib/checkbox';

const Step = Steps.Step;

// 身份证的正则表达式
const SID_REG = /^[1-9]\d{7}((0\d)|(1[0-2]))(([0|1|2]\d)|3[0-1])\d{3}$|^[1-9]\d{5}[1-9]\d{3}((0\d)|(1[0-2]))(([0|1|2]\d)|3[0-1])\d{3}([0-9]|X)$/;

const typeList = [
  {
    value: 1,
    key: '1',
    name: '身份证',
  },
  // {
  //   value: 2,
  //   key: '2',
  //   name: '护照',
  // },
  // {
  //   value: 3,
  //   key: '3',
  //   name: '军官证',
  // },
  // {
  //   value: 4,
  //   key: '4',
  //   name: '回乡证',
  // },
  // {
  //   value: 5,
  //   key: '5',
  //   name: '台胞证',
  // },
  // {
  //   value: 6,
  //   key: '6',
  //   name: '警官证',
  // },
  // {
  //   value: 7,
  //   key: '7',
  //   name: '士兵证',
  // },
  // {
  //   value: 8,
  //   key: '8',
  //   name: '户口簿',
  // },
  // {
  //   value: 9,
  //   key: '9',
  //   name: '港澳内地通行证',
  // },
  // {
  //   value: 10,
  //   key: '10',
  //   name: '临时身份证',
  // },
  // {
  //   value: 11,
  //   key: '11',
  //   name: '外国人居留证',
  // },
  // {
  //   value: 99,
  //   key: '99',
  //   name: '其他',
  // },
];

interface IRegisterStep1Props extends FormComponentProps {
  account: IAccountInfo | undefined;
  userInfo?: IAgentInfo | IDrugStoreInfo;
  logicStep: ERegisterStep | EEnterpriseRegisterStep;
  submitted?: (newStep?: ERegisterStep | EEnterpriseRegisterStep) => void;
}

interface IIndividualRegisterState {
  pending: boolean;
  disableEndDate: boolean;
}

class CreateIndividualForm extends React.Component<IRegisterStep1Props, IIndividualRegisterState> {
  private walletService = WalletService;
  private readonly memberType = EAccountMemberType.Individual;

  constructor(props: IRegisterStep1Props) {
    super(props);
    this.state = {
      pending: false,
      disableEndDate: false,
    };
  }

  async componentDidMount() {
    const { logicStep, account } = this.props;
    if (!!account && logicStep === ERegisterStep.Created) {
      const { setFieldsValue } = this.props.form;
      const beginDate = account.identity_begin_date ? moment(account.identity_begin_date, 'YYYY-MM-DD') : undefined;
      const endDate = account.identity_end_date
        ? account.identity_end_date === '9999-12-31'
          ? undefined
          : moment(account.identity_end_date, 'YYYY-MM-DD')
        : undefined;
      setFieldsValue({
        name: account.name,
        identity_type: account.identity_type || 1,
        identity_no: account.identity_no,
        identity_begin_date: beginDate,
        identity_end_date: endDate,
      });
      if (!account.identity_end_date || account.identity_end_date === '9999-12-31') {
        // 截至日期不存在 或者 为 9999-12-31 默认勾选长期
        this.setState({ disableEndDate: true });
      }
    }
    await LocationService.initArea();
  }

  disabledStartDate = (fieldStartValue: moment.Moment | null) => {
    if (!fieldStartValue) {
      return false;
    }
    const fieldEndValue = this.props.form.getFieldValue('identity_end_date') as moment.Moment;
    // 计算零点时间
    const startValue = moment(fieldStartValue.format('YYYY-MM-DD'), 'YYYY-MM-DD');
    const endValue = fieldEndValue ? moment(fieldEndValue.format('YYYY-MM-DD'), 'YYYY-MM-DD') : moment('9999-12-31', 'YYYY-MM-DD');
    return startValue.valueOf() >= endValue.valueOf();
  };

  disabledEndDate = (fieldEndValue: moment.Moment | null) => {
    const fieldStartValue = this.props.form.getFieldValue('identity_begin_date');
    if (!fieldEndValue || !fieldStartValue) {
      return false;
    }
    // 计算零点时间
    const startValue = moment(fieldStartValue.format('YYYY-MM-DD'), 'YYYY-MM-DD');
    const endValue = moment(fieldEndValue.format('YYYY-MM-DD'), 'YYYY-MM-DD');
    return endValue.valueOf() <= startValue.valueOf();
  };

  async checkStep() {
    const result = await this.walletService.getAccounts();
    return result.find((a) => a.member_type === this.memberType);
  }

  submitStep1 = (e: React.SyntheticEvent) => {
    e.preventDefault();
    this.props.form.validateFields(async (err, values) => {
      if (!err) {
        const step = await this.submitAll(values);
        if (this.props.submitted) {
          this.props.submitted(step);
        }
      }
    });
  };

  async submitAll(values: Partial<IAccountInfo>) {
    const { account } = this.props;
    let logicStep = account ? account.step : undefined;
    try {
      this.setState({ pending: true });
      if (logicStep === ERegisterStep.Created) {
        logicStep = await this.submitPhone(values);
      }

      if (logicStep === ERegisterStep.Phone) {
        logicStep = await this.submitName(values, account?.phone);
      }
    } finally {
      this.setState({ pending: false });
    }
    return logicStep;
  }

  async submitPhone(values: Partial<IAccountInfo>) {
    try {
      const operatorData = {
        verification_code: values.verification_code,
        phone: values.phone,
        step: ERegisterStep.Phone,
        member_type: this.memberType,
      };
      const result = await this.walletService.registerPhone(operatorData);
      if (!!result) {
        const step = await this.checkStep();
        return step ? step.step : undefined;
      }
    } catch (error) {
      if (!!error.response) {
        message.error(JSON.stringify(error.response.data));
      } else {
        message.error(I18N.get('err_submit_failure'));
      }
    }

    return undefined;
  }

  async submitName(values: Partial<IAccountInfo>, backupPhone?: string) {
    const { userInfo } = this.props;
    try {
      if (!values.phone) {
        values.phone = backupPhone;
      }
      const completionInfo = WalletService.buildAccountcompletionInfo(this.memberType, values, userInfo);
      if (completionInfo) {
        const { address, telephone, profession_no, identity_begin_date, identity_end_date } = completionInfo;
        const operatorData = {
          name: values.name,
          identity_type: values.identity_type || 1,
          identity_no: values.identity_no,
          step: ERegisterStep.Name,
          member_type: this.memberType,
          address,
          telephone,
          identity_begin_date,
          identity_end_date,
          profession_no,
        };
        const result = await this.walletService.registerName(operatorData);
        if (result) {
          const step = await this.checkStep();
          return step ? step.step : undefined;
        }
      }
    } catch (error) {
      if (!!error.response) {
        if (error.response.data.status === 'failed' && error.response.data.exec === 'ARRIVED MAX CALL') {
          message.error('认证失败，每天最多只能尝试5次！');
        } else if (error.response.data.code) {
          message.error(error.response.data.detail);
        } else {
          message.error(JSON.stringify(error.response.data));
        }
      } else {
        message.error(I18N.get('err_submit_failure'));
      }
    }

    return undefined;
  }

  async verifyCode(data: IVerificationCode) {
    if (data.mobile) {
      const res = await this.walletService.verifyCode(data.mobile, this.memberType);
      if (!!res) {
        message.success(I18N.get('login_get_verify'));
      }
    }
  }

  /**
   * 起始日期格式验证
   */
  startDateValidator(rule: any, value: string, callback: any) {
    if (isString(value)) {
      const dateReg = /^\d{4}\-\d{2}-/;
      if (dateReg) {
        callback('日期格式不正确, 例: XXXX-XX-XX');
      }
    }
    callback();
  }

  handleEndDateCheck = (e: CheckboxChangeEvent) => {
    const checked = e.target.checked;
    this.props.form.setFieldsValue({ identity_end_date: null });
    this.setState(
      {
        disableEndDate: checked,
      },
      () => {
        this.props.form.validateFields(['identity_end_date'], { force: true });
      }
    );
  };

  endDateValidator = (rule: any, value: string, callback: any) => {
    const { logicStep } = this.props;
    const isReadonly = logicStep > EEnterpriseRegisterStep.Created;
    if (!value && !this.state.disableEndDate && !isReadonly) {
      callback('请输入证件有效截止日期');
    } else {
      callback();
    }
  };

  sidValidator = (rule: any, value: string, callback: any) => {
    const { logicStep } = this.props;
    const isReadonly = logicStep > EEnterpriseRegisterStep.Created;
    const { getFieldValue } = this.props.form;
    const identityType = Number(getFieldValue('identity_type')) || 1;
    if (identityType === 1 && !SID_REG.test(value) && !isReadonly) {
      callback('请输入15位或18位身份证号码');
    } else {
      callback();
    }
  };

  render() {
    const { logicStep, userInfo, account } = this.props;
    const { getFieldDecorator, getFieldValue } = this.props.form;
    const { disableEndDate } = this.state;
    const formItemLayout: FormItemProps = {
      labelCol: { span: 6 },
      wrapperCol: { span: 8 },
    };

    const identityType = Number(getFieldValue('identity_type')) || 1;
    const helpText = identityType === 1 ? '请检查确认信息无误(15位或18位身份证号码)，每天最多只能提交5次' : '请检查确认信息无误，每天最多只能提交5次';

    let isReadonly = false;
    if (!!account) {
      const { step_checking } = account;
      if (!!step_checking) {
        if (!step_checking[ERegisterStep.Phone] && step_checking[ERegisterStep.Name]) {
          // 手机号已解绑的情况
          isReadonly = true;
        }
      }
    }

    return (
      <Row gutter={24} type="flex" justify="center" style={{ marginTop: 60 }}>
        <Col span={16} offset={4}>
          <Form onSubmit={this.submitStep1}>
            {logicStep === ERegisterStep.Created && (
              <Form.Item label={I18N.get('common_mobile')} {...formItemLayout}>
                {getFieldDecorator(`phone`, {
                  rules: [
                    {
                      required: false,
                      message: `${I18N.get('common_input')}${I18N.get('common_mobile')}`,
                    },
                  ],
                  initialValue: !!userInfo ? userInfo.admin_mobile : null,
                })(<Input disabled={true} placeholder={`${I18N.get('common_input')}${I18N.get('common_mobile')}`} />)}
              </Form.Item>
            )}
            {logicStep === ERegisterStep.Created && (
              <Form.Item label={I18N.get('login_verify_code')} {...formItemLayout}>
                {getFieldDecorator(`verification_code`, {
                  rules: [
                    {
                      required: true,
                      message: `${I18N.get('common_input')}${I18N.get('login_verify_code')}`,
                    },
                  ],
                  initialValue: null,
                })(
                  <Input
                    placeholder={`${I18N.get('common_input')}${I18N.get('login_verify_code')}`}
                    suffix={
                      <TencentCaptchaButton isDomestic={true} phoneOrEmail={this.props.form.getFieldValue(`phone`)} onSend={(data) => this.verifyCode(data)} />
                    }
                  />
                )}
              </Form.Item>
            )}

            {logicStep >= ERegisterStep.Created && (
              <Form.Item label={I18N.get('common_name')} {...formItemLayout}>
                {getFieldDecorator(`name`, {
                  rules: [
                    {
                      required: !isReadonly,
                      message: `${I18N.get('common_input')}${I18N.get('common_name')}`,
                    },
                  ],
                  initialValue: null,
                })(<Input disabled={isReadonly} placeholder={`${I18N.get('common_input')}${I18N.get('common_name')}`} />)}
              </Form.Item>
            )}

            {logicStep >= ERegisterStep.Created && (
              <Form.Item label={'证件类型'} {...formItemLayout}>
                {getFieldDecorator(`identity_type`, {
                  rules: [
                    {
                      required: !isReadonly,
                      message: `${I18N.get('common_choose')}${I18N.get('userAdmin_role')}`,
                    },
                  ],
                  initialValue: 1,
                })(
                  <Select disabled={true} placeholder={`${I18N.get('common_choose')}${I18N.get('userAdmin_role')}`}>
                    {typeList.map((option) => {
                      return (
                        <Select.Option key={option.key} value={option.value}>
                          {option.name}
                        </Select.Option>
                      );
                    })}
                  </Select>
                )}
              </Form.Item>
            )}

            {logicStep >= ERegisterStep.Created && (
              <Form.Item label={'证件号'} help={helpText} {...formItemLayout}>
                {getFieldDecorator('identity_no', {
                  rules: [
                    {
                      required: !isReadonly,
                      message: `${I18N.get('common_input')}${'证件号'}`,
                    },
                    {
                      validator: this.sidValidator,
                    },
                  ],
                  initialValue: null,
                })(<Input disabled={isReadonly} placeholder={`${I18N.get('common_input')}`} />)}
              </Form.Item>
            )}
            {logicStep >= ERegisterStep.Created && (
              <React.Fragment>
                <Form.Item label={'证件有效开始日期'} {...formItemLayout} key={'identity_start_date'}>
                  {getFieldDecorator('identity_begin_date', {
                    initialValue: null,
                    rules: [
                      {
                        required: !isReadonly,
                        message: '请填写证件有效开始日期',
                      },
                    ],
                  })(
                    <DatePicker
                      disabled={isReadonly}
                      style={{ width: '100%' }}
                      showToday={false}
                      disabledDate={this.disabledStartDate}
                      format="YYYY-MM-DD"
                      placeholder="开始日期"
                    />
                  )}
                </Form.Item>

                <Form.Item required={true} label={'证件有效截止日期'} {...formItemLayout} key={'identity_end_date'}>
                  <Row type="flex" align="middle">
                    {getFieldDecorator('identity_end_date', {
                      initialValue: null,
                      rules: [
                        {
                          validator: this.endDateValidator,
                        },
                      ],
                    })(
                      <DatePicker
                        disabled={disableEndDate || isReadonly}
                        style={{ flex: '1 1 auto' }}
                        showToday={false}
                        disabledDate={this.disabledEndDate}
                        format="YYYY-MM-DD"
                        placeholder="截止日期"
                      />
                    )}
                    <Checkbox style={{ marginLeft: 8 }} disabled={isReadonly} checked={disableEndDate} onChange={this.handleEndDateCheck}>
                      长期
                    </Checkbox>
                  </Row>
                </Form.Item>
              </React.Fragment>
            )}
            {logicStep >= ERegisterStep.Created && (
              <Form.Item help="脉景收款账户由“通联支付”提供资金存管服务。" wrapperCol={{ span: 12, offset: 6 }}>
                <Button type="primary" htmlType="submit" loading={this.state.pending}>
                  {I18N.get('common_submit') + '并签署《三方协议》'}
                </Button>
              </Form.Item>
            )}
          </Form>
        </Col>
      </Row>
    );
  }
}

const IndividualForm = Form.create<IRegisterStep1Props>()(CreateIndividualForm);

// function beforeUpload(file: File) {
//   return false;
// }

interface IEnterpriseRegisterState {
  pending: boolean;
  fetchingBank: boolean;
  fetchingBranch: boolean;
  bankIndex: string;
  bankName: string;
  branchName: string;
  banks: Array<{ name: string; index: string }>;
  branchs: Array<{ name: string; code: string }>;
  disableEndDate: boolean;
  /** 自定义支行 */
  isCustomer: boolean;
}
class CreateEnterpriseForm extends React.Component<IRegisterStep1Props, IEnterpriseRegisterState> {
  private walletService = WalletService;
  private readonly memberType = EAccountMemberType.Enterprise;
  private flexService = FlexApiService;
  private bucket: IBucketInfo | undefined;

  constructor(props: IRegisterStep1Props) {
    super(props);
    this.state = {
      pending: false,
      fetchingBank: false,
      fetchingBranch: false,
      bankName: '',
      bankIndex: '',
      branchName: '',
      banks: [],
      branchs: [],
      disableEndDate: false,
      isCustomer: false,
    };
    this.fetchBranch = debounce(this.fetchBranch, 800);
  }

  disabledStartDate = (fieldStartValue: moment.Moment | null) => {
    if (!fieldStartValue) {
      return false;
    }
    const fieldEndValue = this.props.form.getFieldValue('identity_end_date') as moment.Moment;
    // 计算零点时间
    const startValue = moment(fieldStartValue.format('YYYY-MM-DD'), 'YYYY-MM-DD');
    const endValue = fieldEndValue ? moment(fieldEndValue.format('YYYY-MM-DD'), 'YYYY-MM-DD') : moment('9999-12-31', 'YYYY-MM-DD');
    return startValue.valueOf() >= endValue.valueOf();
  };

  disabledEndDate = (fieldEndValue: moment.Moment | null) => {
    const fieldStartValue = this.props.form.getFieldValue('identity_begin_date');
    if (!fieldEndValue || !fieldStartValue) {
      return false;
    }
    // 计算零点时间
    const startValue = moment(fieldStartValue.format('YYYY-MM-DD'), 'YYYY-MM-DD');
    const endValue = moment(fieldEndValue.format('YYYY-MM-DD'), 'YYYY-MM-DD');
    return endValue.valueOf() <= startValue.valueOf();
  };

  componentDidMount() {
    const { logicStep, account } = this.props;
    if (!!account && logicStep === EEnterpriseRegisterStep.Info) {
      const { setFieldsValue } = this.props.form;
      const beginDate = account.identity_begin_date ? moment(account.identity_begin_date, 'YYYY-MM-DD') : undefined;
      const endDate = account.identity_end_date
        ? account.identity_end_date === '9999-12-31'
          ? undefined
          : moment(account.identity_end_date, 'YYYY-MM-DD')
        : undefined;
      setFieldsValue({
        name: account.name,
        bank_name: account.bank_name,
        branch_bank_name: account.branch_bank_name,
        legal_name: account.legal_name,
        legal_phone: account.legal_phone,
        business_license: account.business_license,
        enterprise_auth_type: account.enterprise_auth_type,
        identity_type: account.identity_type || 1,
        identity_no: account.identity_no,
        organization_code: account.organization_code,
        payment_bank_code: account.payment_bank_code,
        public_account: account.public_account,
        pic_id_card_back: account.pic_id_card_back,
        pic_business_license: account.pic_business_license,
        pic_id_card_front: account.pic_id_card_front,
        pic_organization_code_certification: account.pic_organization_code_certification,
        pic_tax_registration_certification: account.pic_tax_registration_certification,
        unified_social_credit_code: account.unified_social_credit_code,
        tax_registration_certificate: account.tax_registration_certificate,
        identity_begin_date: beginDate,
        identity_end_date: endDate,
      });
      if (!account.identity_end_date || account.identity_end_date === '9999-12-31') {
        // 截至日期不存在 或者 为 9999-12-31 默认勾选长期
        this.setState({ disableEndDate: true });
      }
    }
    this.fetchBank();
    LocationService.initArea();
  }

  fetchBank = () => {
    const { banks } = this.state;
    if (banks.length > 0) {
      return;
    }

    this.setState({ banks: [], fetchingBank: true });
    this.walletService.getMasterBanks().then((results) => {
      const data = results.map((bank: { name: string; index: string }) => ({
        name: bank.name,
        index: bank.index,
      }));
      this.setState({ banks: data, fetchingBank: false });
    });
  };

  fetchBranch = (value: string) => {
    const { bankIndex } = this.state;
    if (!bankIndex) {
      this.setState({ branchs: [], fetchingBranch: false });
      return;
    }

    this.setState({ branchs: [], fetchingBranch: true });
    this.walletService.getBranchBanks({ index: bankIndex, name: value || '' }).then((results) => {
      const data = results.map((bank: { name: string; code: string }) => ({
        name: bank.name,
        code: bank.code,
      }));
      this.setState({ branchs: data, fetchingBranch: false });
    });
  };

  /** 切换了总行 */
  handleChange = (value: any) => {
    const { banks } = this.state;
    const bank = banks.find((b) => b.index === value);
    this.setState({
      bankName: bank ? bank.name : '',
      bankIndex: value,
      fetchingBank: false,
      branchName: '',
      branchs: [],
      fetchingBranch: false,
    });

    const { setFieldsValue } = this.props.form;
    setFieldsValue({ payment_bank_code: '' });
    this.fetchBranch('');
  };

  /** 切换了支行 */
  handleChangeBranch = (value: any) => {
    const { branchs } = this.state;
    const branch = branchs.find((b) => b.code === value);
    this.setState({
      branchName: branch ? branch.name : '',
      fetchingBranch: false,
    });
  };

  async checkStep() {
    const result = await this.walletService.getAccounts();
    return result.find((a) => a.member_type === this.memberType);
  }

  submitStep1 = (e: React.SyntheticEvent) => {
    e.preventDefault();
    this.props.form.validateFields(async (err, values) => {
      if (!err) {
        const step = await this.submitAll(values);
        if (this.props.submitted) {
          this.props.submitted(step);
        }
      }
    });
  };

  sleep(t: number): Promise<void> {
    return new Promise((resolve) => {
      setTimeout(() => resolve(), t);
    });
  }

  async submitAll(values: any) {
    this.setState({ pending: true });
    const { account } = this.props;
    let logicStep = account ? account.step : undefined;
    let success: boolean | undefined | null;

    try {
      if (logicStep === EEnterpriseRegisterStep.Created) {
        logicStep = await this.submitInfo(values);
        if (logicStep === undefined) {
          // 提交失败
          return;
        }

        for (let i = 0; i < 60; i++) {
          await this.sleep(1000);
          try {
            const res = await this.checkStep();
            if (!res) {
              continue;
            }

            success = res.authenticate_success;
            if (res.step === EEnterpriseRegisterStep.Created) {
              if (res.authenticate_success === null || res.authenticate_success === undefined) {
                // pending状态继续轮询
                continue;
              } else {
                // 已经失败
                break;
              }
            } else if (res.step === EEnterpriseRegisterStep.Info) {
              // 已经成功
              logicStep = res.step;
              break;
            }
          } catch {
            continue;
          }
        }
      }

      if (logicStep === EEnterpriseRegisterStep.Info) {
        logicStep = await this.submitPhone(values);
      } else if (logicStep === EEnterpriseRegisterStep.Created) {
        // 要么超时，要么没成功
        if (success === null || success === undefined) {
          message.error('认证超时');
        } else if (success === false) {
          message.error('认证失败');
        }
      }
    } finally {
      this.setState({ pending: false });
    }

    return logicStep;
  }

  async submitPhone(values: IAccountInfo) {
    try {
      const operatorData = {
        verification_code: values.verification_code,
        phone: values.phone, // 管理员手机和绑定手机保持一致
        step: EEnterpriseRegisterStep.Phone,
        member_type: this.memberType,
      };
      const result = await this.walletService.registerPhone(operatorData);
      if (!!result) {
        const step = await this.checkStep();
        return step ? step.step : undefined;
      }
    } catch (error) {
      if (!!error.response) {
        message.error(JSON.stringify(error.response.data));
      } else {
        message.error(I18N.get('err_submit_failure'));
      }
    }

    return undefined;
  }

  async submitInfo(values: IAccountInfo) {
    const { userInfo } = this.props;
    try {
      const { branchName, bankName, isCustomer } = this.state;
      const completionInfo = WalletService.buildAccountcompletionInfo(this.memberType, values, userInfo);
      if (completionInfo) {
        const { address, telephone, identity_begin_date, identity_end_date } = completionInfo;
        const operatorData: IAccountInfo = {
          name: values.name,
          enterprise_auth_type: Number(values.enterprise_auth_type),
          legal_name: values.legal_name,
          legal_phone: values.legal_phone,
          identity_type: values.identity_type || 1,
          identity_no: values.identity_no,
          public_account: values.public_account,
          bank_name: bankName,
          branch_bank_name: !isCustomer ? branchName : values.branch_bank_name,
          payment_bank_code: values.payment_bank_code,
          pic_business_license: values.pic_business_license,
          pic_id_card_front: values.pic_id_card_front,
          pic_id_card_back: values.pic_id_card_back,
          step: EEnterpriseRegisterStep.Info,
          member_type: this.memberType,
          address,
          telephone,
          identity_begin_date,
          identity_end_date,
        };

        if (operatorData.enterprise_auth_type === 1) {
          operatorData.business_license = values.business_license;
          operatorData.organization_code = values.organization_code;
          operatorData.tax_registration_certificate = values.tax_registration_certificate;
          operatorData.pic_tax_registration_certification = values.pic_tax_registration_certification;
          operatorData.pic_organization_code_certification = values.pic_organization_code_certification;
        } else {
          operatorData.unified_social_credit_code = values.unified_social_credit_code;
        }

        const result = await this.walletService.registerName(operatorData);
        if (result) {
          const step = await this.checkStep();
          return step ? step.step : undefined;
        }
      }
    } catch (error) {
      if (!!error.response) {
        if (error.response.data.status === 'failed' && error.response.data.exec === 'ARRIVED MAX CALL') {
          message.error('认证失败，每天最多只能尝试5次！');
        } else {
          message.error(JSON.stringify(error.response.data));
        }
      } else {
        message.error(I18N.get('err_submit_failure'));
      }
    }

    return undefined;
  }

  async verifyCode(data: IVerificationCode) {
    if (data.mobile) {
      const res = await this.walletService.verifyCode(data.mobile, this.memberType);
      if (!!res) {
        message.success(I18N.get('login_get_verify'));
      }
    }
  }

  handleEndDateCheck = (e: CheckboxChangeEvent) => {
    const checked = e.target.checked;
    this.props.form.setFieldsValue({ identity_end_date: null });
    this.setState(
      {
        disableEndDate: checked,
      },
      () => {
        this.props.form.validateFields(['identity_end_date'], { force: true });
      }
    );
  };

  handleIsCustomerCheck = (e: CheckboxChangeEvent) => {
    const checked = e.target.checked;
    this.setState(
      {
        isCustomer: checked,
        branchName: '',
      },
      () => {
        this.props.form.setFieldsValue({ branch_bank_name: undefined, payment_bank_code: undefined });
      }
    );
  };

  endDateValidator = (rule: any, value: string, callback: any) => {
    const { logicStep } = this.props;
    const isReadonly = logicStep > EEnterpriseRegisterStep.Created;
    if (!value && !this.state.disableEndDate && !isReadonly) {
      callback('请输入证件有效截止日期');
    } else {
      callback();
    }
  };

  sidValidator = (rule: any, value: string, callback: any) => {
    const { logicStep } = this.props;
    const isReadonly = logicStep > EEnterpriseRegisterStep.Created;
    const { getFieldValue } = this.props.form;
    const identityType = Number(getFieldValue('identity_type')) || 1;
    if (identityType === 1 && !SID_REG.test(value) && !isReadonly) {
      callback('请输入15位或18位身份证号码');
    } else {
      callback();
    }
  };

  render() {
    const { logicStep, userInfo } = this.props;
    const { fetchingBank, fetchingBranch, banks, branchs, disableEndDate, isCustomer } = this.state;
    const { getFieldDecorator, getFieldValue } = this.props.form;
    const formItemLayout: FormItemProps = {
      labelCol: { span: 6 },
      wrapperCol: { span: 8 },
    };

    const isThreeLicense = Number(getFieldValue('enterprise_auth_type')) === 1;
    const isReadonly = logicStep > EEnterpriseRegisterStep.Created;
    const identityType = Number(getFieldValue('identity_type')) || 1;
    const helpText = identityType === 1 ? '请检查确认信息无误(15位或18位身份证号码)，每天最多只能提交5次' : '请检查确认信息无误，每天最多只能提交5次';

    const formItems: JSX.Element[] = [];

    const branchBankTips = (
      <span>
        如没有对应支行，可以手动输入支行名称和12位行号，行号可以通过.
        <a target="_blank" href="https://e.czbank.com/CORPORBANK/query_unionBank_index.jsp">
          此链接查询
        </a>
        ，或向发卡行询问
      </span>
    );

    formItems.push(
      <Form.Item label={'企业名称'} {...formItemLayout} key={'name'}>
        {getFieldDecorator(`name`, {
          rules: [
            {
              required: !isReadonly,
              message: `请输入企业全称`,
            },
          ],
          initialValue: null,
        })(<Input disabled={isReadonly} placeholder={`请输入企业全称`} />)}
      </Form.Item>
    );

    formItems.push(
      <Form.Item label="认证类型" {...formItemLayout} key={'enterprise_auth_type'}>
        {getFieldDecorator(`enterprise_auth_type`, {
          rules: [
            {
              required: !isReadonly,
              message: `请选择认证类型`,
            },
          ],
          initialValue: '2',
        })(
          <Radio.Group disabled={isReadonly}>
            <Radio.Button value="1">三证</Radio.Button>
            <Radio.Button value="2">一证</Radio.Button>
          </Radio.Group>
        )}
      </Form.Item>
    );

    {
      formItems.push(
        <Form.Item label={'统一社会信用码'} style={{ display: isThreeLicense ? 'none' : undefined }} {...formItemLayout} key={'unified_social_credit_code'}>
          {getFieldDecorator(`unified_social_credit_code`, {
            rules: [
              {
                required: !isReadonly && !isThreeLicense,
                message: `请输入统一社会信用码`,
              },
            ],
            initialValue: null,
          })(<Input disabled={isReadonly} placeholder={`请输入统一社会信用码`} />)}
        </Form.Item>
      );

      formItems.push(
        <Form.Item label={'营业执照号'} style={{ display: isThreeLicense ? undefined : 'none' }} {...formItemLayout} key={'business_license'}>
          {getFieldDecorator(`business_license`, {
            rules: [
              {
                required: !isReadonly && isThreeLicense,
                message: `请输入营业执照号`,
              },
            ],
            initialValue: null,
          })(<Input disabled={isReadonly} placeholder={`请输入营业执照号`} />)}
        </Form.Item>
      );

      formItems.push(
        <Form.Item label={'组织机构代码'} style={{ display: isThreeLicense ? undefined : 'none' }} {...formItemLayout} key={'organization_code'}>
          {getFieldDecorator(`organization_code`, {
            rules: [
              {
                required: !isReadonly && isThreeLicense,
                message: `请输入组织机构代码`,
              },
            ],
            initialValue: null,
          })(<Input disabled={isReadonly} placeholder={`请输入组织机构代码`} />)}
        </Form.Item>
      );

      formItems.push(
        <Form.Item label={'税务登记证'} style={{ display: isThreeLicense ? undefined : 'none' }} {...formItemLayout} key={'tax_registration_certificate'}>
          {getFieldDecorator(`tax_registration_certificate`, {
            rules: [
              {
                required: !isReadonly && isThreeLicense,
                message: `请输入税务登记证`,
              },
            ],
            initialValue: null,
          })(<Input disabled={isReadonly} placeholder={`请输入税务登记证`} />)}
        </Form.Item>
      );
    }

    formItems.push(
      <Form.Item label={I18N.get('common_name')} {...formItemLayout} key={'legal_name'}>
        {getFieldDecorator(`legal_name`, {
          rules: [
            {
              required: !isReadonly,
              message: `请输入法人姓名`,
            },
          ],
          initialValue: null,
        })(<Input disabled={isReadonly} placeholder={`请输入法人姓名`} />)}
      </Form.Item>
    );

    formItems.push(
      <Form.Item label={'法人手机号'} {...formItemLayout} key={'legal_phone'}>
        {getFieldDecorator(`legal_phone`, {
          rules: [
            {
              required: !isReadonly,
              message: `${I18N.get('common_input')}${I18N.get('common_mobile')}`,
            },
            {
              pattern: /\d{11}/,
              message: `请输入11位手机号码`,
            },
          ],
          initialValue: null,
        })(<Input disabled={isReadonly} placeholder={`请输入法人手机号码`} />)}
      </Form.Item>
    );

    formItems.push(
      <Form.Item label={'证件类型'} {...formItemLayout} key={'identity_type'}>
        {getFieldDecorator(`identity_type`, {
          rules: [
            {
              required: !isReadonly,
              message: `${I18N.get('common_choose')}${I18N.get('userAdmin_role')}`,
            },
          ],
          initialValue: 1,
        })(
          <Select disabled={true} placeholder={`${I18N.get('common_choose')}${I18N.get('userAdmin_role')}`}>
            {typeList.map((option) => {
              return (
                <Select.Option key={option.key} value={option.value}>
                  {option.name}
                </Select.Option>
              );
            })}
          </Select>
        )}
      </Form.Item>
    );

    formItems.push(
      <Form.Item label={'证件号'} key={'identity_no'} help={helpText} {...formItemLayout}>
        {getFieldDecorator('identity_no', {
          rules: [
            {
              required: !isReadonly,
              message: `${I18N.get('common_input')}${'证件号'}`,
            },
            {
              validator: this.sidValidator,
            },
          ],
          initialValue: null,
        })(<Input disabled={isReadonly} placeholder={`${I18N.get('common_input')}`} />)}
      </Form.Item>
    );

    formItems.push(
      <Form.Item label={'证件有效开始日期'} {...formItemLayout} key={'identity_start_date'}>
        {getFieldDecorator('identity_begin_date', {
          initialValue: null,
          rules: [
            {
              required: !isReadonly,
              message: '请填写证件有效开始日期',
            },
          ],
        })(
          <DatePicker
            disabled={isReadonly}
            style={{ width: '100%' }}
            showToday={false}
            disabledDate={this.disabledStartDate}
            format="YYYY-MM-DD"
            placeholder="开始日期"
          />
        )}
      </Form.Item>
    );

    formItems.push(
      <Form.Item required={true} label={'证件有效截止日期'} {...formItemLayout} key={'identity_end_date'}>
        <Row type="flex" align="middle">
          {getFieldDecorator('identity_end_date', {
            initialValue: null,
            rules: [
              {
                validator: this.endDateValidator,
              },
            ],
          })(
            <DatePicker
              disabled={disableEndDate || isReadonly}
              style={{ flex: '1 1 auto' }}
              showToday={false}
              disabledDate={this.disabledEndDate}
              format="YYYY-MM-DD"
              placeholder="截止日期"
            />
          )}
          <Checkbox style={{ marginLeft: 8 }} disabled={isReadonly} checked={disableEndDate} onChange={this.handleEndDateCheck}>
            长期
          </Checkbox>
        </Row>
      </Form.Item>
    );

    formItems.push(
      <Form.Item label={'企业对公账户'} {...formItemLayout} key={'public_account'}>
        {getFieldDecorator(`public_account`, {
          rules: [
            {
              required: !isReadonly,
              message: `请输入企业对公账户`,
            },
          ],
          initialValue: null,
        })(<Input disabled={isReadonly} placeholder={`请输入企业对公账户`} />)}
      </Form.Item>
    );

    if (isReadonly) {
      formItems.push(
        <Form.Item label={'开户银行'} {...formItemLayout} key={'bank_name'}>
          {getFieldDecorator(`bank_name`, {
            initialValue: null,
            rules: [{ required: false }],
          })(<Input disabled={true} />)}
        </Form.Item>
      );
      formItems.push(
        <Form.Item label={'开户支行'} {...formItemLayout} key={'branch_bank_name'}>
          {getFieldDecorator(`branch_bank_name`, {
            initialValue: null,
            rules: [{ required: false }],
          })(<Input disabled={true} />)}
        </Form.Item>
      );
    } else {
      formItems.push(
        <Form.Item
          label={
            <span>
              开户银行&nbsp;
              <Tooltip title="如没有对应城市的农村商业银行，可以直接选择“农村商业银行”">
                <Icon style={{ color: '#1FB8CD' }} type="exclamation-circle" />
              </Tooltip>
            </span>
          }
          {...formItemLayout}
          key={'bank_index'}
        >
          {getFieldDecorator(`bank_index`, {
            initialValue: undefined,
            rules: [
              {
                required: true,
                message: `请选择开户银行`,
              },
            ],
          })(
            <Select
              placeholder={`请选择开户银行`}
              showSearch={true}
              notFoundContent={fetchingBank ? <Spin size="small" /> : null}
              filterOption={(input, option) => (option.props.children as string).toLowerCase().indexOf(input.toLowerCase()) >= 0}
              onChange={this.handleChange}
              style={{ width: '100%' }}
            >
              {banks.map((r) => (
                <Select.Option key={r.index} value={r.index}>
                  {r.name}
                </Select.Option>
              ))}
            </Select>
          )}
        </Form.Item>
      );

      if (!isCustomer) {
        formItems.push(
          <Form.Item
            label={
              <span>
                开户支行&nbsp;
                <Tooltip title={branchBankTips}>
                  <Icon style={{ color: '#1FB8CD' }} type="exclamation-circle" />
                </Tooltip>
              </span>
            }
            {...formItemLayout}
            key={'payment_bank_code'}
          >
            <Row type="flex" align="middle">
              {getFieldDecorator(`payment_bank_code`, {
                initialValue: undefined,
                rules: [
                  {
                    required: true,
                    message: '请选择开户支行',
                  },
                ],
              })(
                <Select
                  placeholder={`请选择开户支行`}
                  showSearch={true}
                  notFoundContent={fetchingBranch ? <Spin size="small" /> : null}
                  filterOption={false}
                  onSearch={this.fetchBranch}
                  onChange={this.handleChangeBranch}
                  style={{ width: 'unset', flex: '1 1 auto' }}
                >
                  {branchs.map((b) => (
                    <Select.Option key={b.code} value={b.code}>
                      {b.name}
                    </Select.Option>
                  ))}
                </Select>
              )}
              <Checkbox style={{ marginLeft: 8 }} disabled={isReadonly} checked={isCustomer} onChange={this.handleIsCustomerCheck}>
                自定义
              </Checkbox>
            </Row>
          </Form.Item>
        );
      } else {
        formItems.push(
          <Form.Item
            label={
              <span>
                开户支行&nbsp;
                <Tooltip title={branchBankTips}>
                  <Icon style={{ color: '#1FB8CD' }} type="exclamation-circle" />
                </Tooltip>
              </span>
            }
            {...formItemLayout}
            key={'branch_bank_name'}
          >
            <Row type="flex" align="middle">
              {getFieldDecorator(`branch_bank_name`, {
                initialValue: undefined,
                rules: [
                  {
                    required: true,
                    message: '请输入开户支行',
                  },
                ],
              })(<Input disabled={isReadonly} placeholder={`请输入开户支行`} style={{ width: 'unset', flex: '1 1 auto' }} />)}
              <Checkbox style={{ marginLeft: 8 }} disabled={isReadonly} checked={isCustomer} onChange={this.handleIsCustomerCheck}>
                自定义
              </Checkbox>
            </Row>
          </Form.Item>
        );
        formItems.push(
          <Form.Item label={'行号'} {...formItemLayout} key={'payment_bank_code'}>
            <Row type="flex" align="middle">
              {getFieldDecorator(`payment_bank_code`, {
                initialValue: null,
                rules: [
                  {
                    required: true,
                    max: 12,
                    len: 12,
                    message: '请检查确认信息无误（12位行号）',
                  },
                ],
              })(<Input disabled={isReadonly} placeholder={`通过开户支行自动带入或手动输入`} />)}
            </Row>
          </Form.Item>
        );
      }
    }

    const uploadButton = (
      <div>
        <Icon type={'plus'} />
        <div className="ant-upload-text">上传</div>
      </div>
    );

    const imageUrl = getFieldValue('pic_business_license');
    formItems.push(
      <Form.Item label={'营业执照'} {...formItemLayout} key={'pic_business_license'}>
        {getFieldDecorator(`pic_business_license`, {
          initialValue: null,
          rules: [
            {
              required: !isReadonly,
            },
          ],
        })(
          <Upload
            disabled={isReadonly}
            listType="picture-card"
            className="license-uploader"
            showUploadList={false}
            beforeUpload={(file: File) => {
              this.uploadFile(file, 'pic_business_license');
              return false;
            }}
          >
            {imageUrl ? <img src={imageUrl} style={{ width: '100%' }} /> : uploadButton}
          </Upload>
        )}
      </Form.Item>
    );

    const imageUrlOcc = getFieldValue('pic_organization_code_certification');
    formItems.push(
      <Form.Item label={'组织机构证'} style={{ display: isThreeLicense ? undefined : 'none' }} {...formItemLayout} key={'pic_organization_code_certification'}>
        {getFieldDecorator(`pic_organization_code_certification`, {
          initialValue: null,
          rules: [
            {
              required: !isReadonly && isThreeLicense,
            },
          ],
        })(
          <Upload
            disabled={isReadonly}
            listType="picture-card"
            className="license-uploader"
            showUploadList={false}
            beforeUpload={(file: File) => {
              this.uploadFile(file, 'pic_organization_code_certification');
              return false;
            }}
          >
            {imageUrlOcc ? <img src={imageUrlOcc} style={{ width: '100%' }} /> : uploadButton}
          </Upload>
        )}
      </Form.Item>
    );

    const imageUrlTrc = getFieldValue('pic_tax_registration_certification');
    formItems.push(
      <Form.Item label={'税务登记证'} style={{ display: isThreeLicense ? undefined : 'none' }} {...formItemLayout} key={'pic_tax_registration_certification'}>
        {getFieldDecorator(`pic_tax_registration_certification`, {
          initialValue: null,
          rules: [
            {
              required: !isReadonly && isThreeLicense,
            },
          ],
        })(
          <Upload
            disabled={isReadonly}
            listType="picture-card"
            className="license-uploader"
            showUploadList={false}
            beforeUpload={(file: File) => {
              this.uploadFile(file, 'pic_tax_registration_certification');
              return false;
            }}
          >
            {imageUrlTrc ? <img src={imageUrlTrc} style={{ width: '100%' }} /> : uploadButton}
          </Upload>
        )}
      </Form.Item>
    );

    const imageUrl1 = getFieldValue('pic_id_card_front');
    formItems.push(
      <Form.Item label={'身份证正面照'} {...formItemLayout} key={'pic_id_card_front'}>
        {getFieldDecorator(`pic_id_card_front`, {
          initialValue: null,
          rules: [
            {
              required: !isReadonly,
            },
          ],
        })(
          <Upload
            disabled={isReadonly}
            listType="picture-card"
            className="license-uploader"
            showUploadList={false}
            beforeUpload={(file: File) => {
              this.uploadFile(file, 'pic_id_card_front');
              return false;
            }}
          >
            {imageUrl1 ? <img src={imageUrl1} style={{ width: '100%' }} /> : uploadButton}
          </Upload>
        )}
      </Form.Item>
    );

    const imageUrl2 = getFieldValue('pic_id_card_back');
    formItems.push(
      <Form.Item label={'身份证背面照'} {...formItemLayout} key={'pic_id_card_back'}>
        {getFieldDecorator(`pic_id_card_back`, {
          initialValue: null,
          rules: [
            {
              required: !isReadonly,
            },
          ],
        })(
          <Upload
            disabled={isReadonly}
            listType="picture-card"
            className="license-uploader"
            showUploadList={false}
            beforeUpload={(file: File) => {
              this.uploadFile(file, 'pic_id_card_back');
              return false;
            }}
          >
            {imageUrl2 ? <img src={imageUrl2} style={{ width: '100%' }} /> : uploadButton}
          </Upload>
        )}
      </Form.Item>
    );

    formItems.push(
      <Form.Item label={'管理员手机号'} {...formItemLayout} key={'phone'}>
        {getFieldDecorator(`phone`, {
          rules: [
            {
              required: false,
              message: `${I18N.get('common_input')}${I18N.get('common_mobile')}`,
            },
          ],
          initialValue: !!userInfo ? userInfo.admin_mobile : null,
        })(<Input disabled={true} placeholder={`后续需要手机验证码`} />)}
      </Form.Item>
    );

    formItems.push(
      <Form.Item label={I18N.get('login_verify_code')} {...formItemLayout} key={'login_verify_code'}>
        {getFieldDecorator(`verification_code`, {
          rules: [
            {
              required: true,
              message: `${I18N.get('common_input')}${I18N.get('login_verify_code')}`,
            },
          ],
          initialValue: null,
        })(
          <Input
            placeholder={`${I18N.get('common_input')}${I18N.get('login_verify_code')}`}
            suffix={<TencentCaptchaButton isDomestic={true} phoneOrEmail={this.props.form.getFieldValue(`phone`)} onSend={(data) => this.verifyCode(data)} />}
          />
        )}
      </Form.Item>
    );

    formItems.push(
      <Form.Item help="脉景收款账户由“通联支付”提供资金存管服务。" key={'submit'} wrapperCol={{ span: 12, offset: 6 }}>
        <Button type="primary" htmlType="submit" loading={this.state.pending}>
          {I18N.get('common_submit') + '并签署《三方协议》'}
        </Button>
      </Form.Item>
    );

    return (
      <Row gutter={24} type="flex" justify="center" style={{ marginTop: 60 }}>
        <Col span={16} offset={4}>
          <Form onSubmit={this.submitStep1}>{formItems}</Form>
        </Col>
      </Row>
    );
  }

  private async uploadFile(file: File, name: string) {
    try {
      const isJpgOrPng = file.type === 'image/jpeg' || file.type === 'image/png';
      if (!isJpgOrPng) {
        message.error('抱歉，只能上传JPG或PNG图片!');
        return;
      }
      let bucket = this.bucket;
      if (!bucket) {
        this.bucket = await this.flexService.createBucket({
          bucket_type: 'mc_wallet_pic',
          intro: { name: 'macrocura wallet register pictures' },
        });
        bucket = this.bucket;
      }
      if (!!bucket) {
        const res = await this.flexService.uploadJumpLinkPic(bucket, file);
        if (!!res) {
          this.props.form.setFieldsValue({ [name]: res });
          // onSuccess(res, file);
        } else {
          message.error('上传照片失败了!');
        }
      } else {
        message.error('上传照片失败了!');
      }
    } catch (error) {
      message.error('出错了');
    }
  }
}

const EnterpriseForm = Form.create<IRegisterStep1Props>()(CreateEnterpriseForm);

interface IWalletRegisterPageProps extends FormComponentProps, RouteComponentProps {
  isAgent: boolean;
  isDomestic: boolean;
  userInfo: IAgentInfo | IDrugStoreInfo | undefined;
}

interface IWalletRegisterPageState {
  account?: IAccountInfo;
}

class WalletRegisterForm extends React.Component<IWalletRegisterPageProps, IWalletRegisterPageState> {
  private walletService = WalletService;
  readonly memberType: EAccountMemberType;

  constructor(props: IWalletRegisterPageProps) {
    super(props);
    let memberType = EAccountMemberType.Individual;
    if (this.props.location.pathname === '/app/wallet/register/enterprise') {
      memberType = EAccountMemberType.Enterprise;
    }
    this.memberType = memberType;
    this.state = { account: this.walletService.accountInfo.find((a) => a.member_type === this.memberType) };
  }

  async componentDidMount() {
    let { account } = this.state;
    if (!account) {
      await this.checkStep();
      account = this.state.account;
    }

    if (account) {
      const logicStep = account.step;
      const notCreatedStep = this.memberType === EAccountMemberType.Individual ? ERegisterStep.NotCreated : EEnterpriseRegisterStep.NotCreated;
      if (logicStep <= notCreatedStep) {
        this.createAccount();
      }
    } else {
      this.createAccount();
    }
  }

  async getContract() {
    const segments = location.href.split('?');
    const jumpUrl = segments[0] + '?type=' + this.memberType;
    const resContract = await this.walletService.getContract({ jumpUrl, memberType: this.memberType });
    if (resContract) {
      return resContract.url;
    } else {
      return '';
    }
  }

  async createAccount() {
    const result = await this.walletService.createAccount(this.memberType);
    if (!!result) {
      await this.checkStep();
    }
  }

  async checkStep(noRefresh?: boolean) {
    const refresh = !noRefresh;
    const result = await this.walletService.getAccounts();
    if (!!result && refresh) {
      this.setState({ account: result.find((a) => a.member_type === this.memberType) });
    }
    return result.find((a) => a.member_type === this.memberType);
  }

  async gotoSign() {
    const contractUrl = await this.getContract();
    if (!!contractUrl) {
      location.href = contractUrl;
    }
  }

  async checkSign() {
    const info = await this.checkStep();
    const contractStep = this.memberType === EAccountMemberType.Individual ? ERegisterStep.Contract : EEnterpriseRegisterStep.Contract;
    if (info && info.step < contractStep) {
      Modal.confirm({
        title: '提示',
        content: '您还未成功签署协议，请重试！',
        okText: '去阅读',
        onOk: () => this.gotoSign(),
      });
    }
  }

  render() {
    const { account } = this.state;
    const logicStep = account ? account.step : ERegisterStep.NotCreated;
    const guiStep = this.memberType === EAccountMemberType.Individual ? this.mapStep(logicStep) : this.mapEnterpriseStep(logicStep);
    let content: JSX.Element | undefined;
    switch (guiStep) {
      case 0:
        if (this.memberType === EAccountMemberType.Individual) {
          content = (
            <div style={{ marginBottom: 50 }}>
              <IndividualForm
                account={account}
                logicStep={logicStep}
                userInfo={this.props.userInfo}
                submitted={(step) => {
                  if (step === ERegisterStep.Name) {
                    this.gotoSign();
                  } else {
                    this.checkStep();
                  }
                }}
              />
            </div>
          );
        } else {
          content = (
            <div style={{ marginBottom: 50 }}>
              <EnterpriseForm
                userInfo={this.props.userInfo}
                account={account}
                logicStep={logicStep}
                submitted={(step) => {
                  if (step === EEnterpriseRegisterStep.Phone) {
                    this.gotoSign();
                  } else {
                    this.checkStep();
                  }
                }}
              />
            </div>
          );
        }
        break;
      case 1:
        content = (
          <div>
            <Row type="flex" justify="center" style={{ marginTop: 60 }}>
              <Icon style={{ fontSize: 72, color: '#FFD700' }} type="edit" theme="filled" />
            </Row>
            <Row type="flex" justify="center" style={{ marginTop: 16 }}>
              请您阅读并同意相关<a onClick={() => this.gotoSign()}>服务条款和隐私政策</a>
            </Row>
            <Row type="flex" justify="center" style={{ marginTop: 20 }} gutter={24}>
              <Col>
                <Button onClick={() => this.gotoSign()}>{'签署《三方协议》'}</Button>
              </Col>
              <Col>
                <Button onClick={() => this.checkSign()}>{'我已签署'}</Button>
              </Col>
            </Row>
          </div>
        );
        break;
      case 2:
        content = (
          <div>
            <Row type="flex" justify="center" style={{ marginTop: 60 }}>
              <Icon style={{ fontSize: 72, color: '#52C41A' }} type="check-circle" />
            </Row>
            <Row type="flex" justify="center" style={{ marginTop: 16 }}>
              <span>您已完成协议签署，请确认开户！</span>
            </Row>
            <Row type="flex" justify="center" style={{ marginTop: 20 }}>
              <Button type="primary" htmlType="submit" onClick={() => this.confirmOpen()}>
                {'确认开户'}
              </Button>
            </Row>
          </div>
        );
        break;
    }
    return (
      <div>
        <div style={{ margin: 12 }} className="clearfix">
          <span style={{ float: 'left', lineHeight: '32px', color: '#1fb8cd' }}>
            {this.memberType === EAccountMemberType.Enterprise ? '脉景收款账户企业开户' : '脉景收款账户个人开户'}
          </span>
          <Button type="primary" onClick={() => this.goback()} style={{ float: 'right' }}>
            {I18N.get('common_back')}
          </Button>
        </div>
        <Row gutter={24} style={{ margin: 8 }}>
          <Col span={16} offset={4}>
            <Steps current={guiStep}>
              <Step title="填写开户资料" />
              <Step title="签署三方协议" />
              <Step title="完成" />
            </Steps>
          </Col>
        </Row>

        {content}
      </div>
    );
  }

  private goback() {
    this.props.history.push(RouterConfig.Urls.wallet);
  }

  private async confirmOpen() {
    const { account } = this.state;
    const openedStep = this.memberType === EAccountMemberType.Individual ? ERegisterStep.Opened : EEnterpriseRegisterStep.Opened;
    let res = false;
    if (account && account.step === openedStep) {
      res = true;
    } else {
      res = await this.walletService.openAccount(this.memberType);
    }

    if (res) {
      message.success('开户成功！');
      this.goback();
    } else {
      message.error('开户失败！');
    }
  }

  private mapStep(logicStep: ERegisterStep | EEnterpriseRegisterStep) {
    let guiStep = -1;
    switch (logicStep) {
      case ERegisterStep.NotCreated:
        guiStep = 0;
        break;
      case ERegisterStep.Created:
        guiStep = 0;
        break;
      case ERegisterStep.Phone:
        guiStep = 0;
        break;
      case ERegisterStep.Name:
        guiStep = 1;
        break;
      case ERegisterStep.Contract:
        guiStep = 2;
        break;
      case ERegisterStep.Opened:
        guiStep = 2;
        break;
      default:
        guiStep = 0;
        break;
    }
    return guiStep;
  }

  private mapEnterpriseStep(logicStep: ERegisterStep | EEnterpriseRegisterStep) {
    let guiStep = -1;
    switch (logicStep) {
      case EEnterpriseRegisterStep.NotCreated:
        guiStep = 0;
        break;
      case EEnterpriseRegisterStep.Created:
        guiStep = 0;
        break;
      case EEnterpriseRegisterStep.Info:
        guiStep = 0;
        break;
      case EEnterpriseRegisterStep.Phone:
        guiStep = 1;
        break;
      case EEnterpriseRegisterStep.Contract:
        guiStep = 2;
        break;
      case EEnterpriseRegisterStep.Opened:
        guiStep = 2;
        break;
      default:
        guiStep = 0;
        break;
    }
    return guiStep;
  }
}

const WalletRegisterPage = Form.create<IWalletRegisterPageProps>()(WalletRegisterForm);
export default WalletRegisterPage;



// WEBPACK FOOTER //
// ./src/components/views/Wallet/WalletRegisterPage.tsx